import { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check } from 'lucide-react';
import { LINKS, TAGLINE_SECONDARY, CONTRACT_ADDRESS, copyCa } from '@/data/content';

export default function MarketCTA() {
  const [copied, setCopied] = useState(false);

  return (
    <section
      id="market"
      data-testid="market-section"
      className="relative overflow-hidden py-24 sm:py-32"
      aria-label="Join the Toonverse"
    >
      <div className="relative mx-auto max-w-[1100px] px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="scanlines relative overflow-hidden rounded-[2rem] border border-electric/25 p-8 text-center sm:p-14"
          style={{
            background:
              'radial-gradient(80% 100% at 50% 0%, rgba(22,119,255,0.2) 0%, rgba(8,21,43,0.9) 60%), #050B18',
            boxShadow: '0 0 80px rgba(0,200,255,0.16), inset 0 1px 0 rgba(0,200,255,0.25)'
          }}
          data-testid="market-panel"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">05 — MARKET</p>
          <h2
            data-testid="market-title"
            className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl"
          >
            JOIN THE{' '}
            <span className="text-glow-gold bg-gradient-to-r from-gold to-ember bg-clip-text text-transparent">
              TOONVERSE
            </span>
          </h2>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-snow/50 sm:text-[11px]">
            {TAGLINE_SECONDARY}
          </p>

          <div
            className="mx-auto mt-9 flex max-w-lg items-center justify-between gap-4 rounded-2xl border border-electric/35 bg-navy/60 px-5 py-4"
            data-testid="ca-widget"
          >
            <div className="min-w-0 text-left">
              <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-snow/45">CONTRACT ADDRESS</p>
              <p
                className="mt-1 break-all font-mono text-[11px] font-semibold tracking-[0.12em] text-gold sm:text-xs"
                data-testid="ca-value"
              >
                {CONTRACT_ADDRESS}
              </p>
            </div>
            <button
              onClick={async () => setCopied(await copyCa())}
              className="light-sweep flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-electric/40 text-electric transition-all duration-200 hover:border-electric hover:shadow-[0_0_18px_rgba(0,200,255,0.45)]"
              title="Copy contract address"
              data-testid="ca-copy-button"
              aria-label="Copy contract address"
            >
              {copied ? <Check className="h-4 w-4 text-toongreen" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>

          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <motion.a
              href={LINKS.buy}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="market-buy-button"
              className="light-sweep relative w-full rounded-full bg-gradient-to-r from-gold via-ember to-gold px-12 py-5 font-display text-sm font-extrabold uppercase tracking-[0.22em] text-navy shadow-[0_0_44px_rgba(255,181,46,0.55)] sm:w-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 320, damping: 18 }}
            >
              <span className="relative z-10">BUY $ARC TOONS</span>
            </motion.a>
            <motion.a
              href={LINKS.chart}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="market-chart-link"
              className="light-sweep flex w-full items-center justify-center gap-3 rounded-full border border-electric/50 bg-navy/50 px-10 py-5 font-display text-sm font-bold uppercase tracking-[0.22em] text-electric shadow-[0_0_28px_rgba(0,200,255,0.25)] transition-all duration-200 sm:w-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 320, damping: 18 }}
            >
              CHART — DEXSCREENER
            </motion.a>
          </div>

          <p className="mt-8 font-mono text-[9px] uppercase tracking-[0.3em] text-snow/40">
            BUY VIA{' '}
            <a
              href={LINKS.buy}
              target="_blank"
              rel="noopener noreferrer"
              className="text-electric underline-offset-4 hover:underline"
              data-testid="market-argus-link"
            >
              ARGUS.WORLD
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
